// pages/HardBinSummaryPage.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DataGrid, {
  Column,
  FilterRow,
  HeaderFilter,
  SearchPanel,
  Paging,
  Sorting,
  Export
} from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import notify from 'devextreme/ui/notify';
import 'devextreme/dist/css/dx.material.blue.light.css';
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config';

const HARD_BIN_API_URL_NEW = `${API_URL}/api/oracle/hard-bin-summary-new`; 
//const CUST_API_URL = `${API_URL}/api/oracle/common/custs`;
//const DEVICE_API_URL = `${API_URL}/api/oracle/common/devicesALL`;

const CUST_API_URL = `${API_URL}/api/local/common/custs`;
const DEVICE_API_URL = `${API_URL}/api/local/common/devices`;



export default function HardBinSummaryPage({ user }) {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [data, setData] = useState([]);
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  
  const [selectedFactory, setSelectedFactory] = useState('FT01');
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState('ALL');
  
  const [loading, setLoading] = useState(false);
  const [custLoading, setCustLoading] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(false);

  const [error, setError] = useState("");
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);

  const formatDate = (date) => {
    if (!date) return '';
    const y = date.getFullYear().toString();
    const m = (date.getMonth() + 1).toString().padStart(2, "0");
    const d = date.getDate().toString().padStart(2, "0");
    return `${y}${m}${d}`;
  };

  useEffect(() => {
    if (!selectedFactory) {
      setCustList([]);
      return;
    }
    setCustLoading(true);
    axios.get(CUST_API_URL, { params: { factory: selectedFactory } })
      .then(res => { if (res.data.retCode === 0) setCustList(res.data.data || []); else setCustList([]); })
      .catch(e => { console.error("CUST 목록 로드 오류:", e); setCustList([]); })
      .finally(() => setCustLoading(false));
  }, [selectedFactory]);
  
  useEffect(() => {
    if (!selectedFactory || !selectedCust) {
      setDeviceList([]);
      setSelectedDevice('ALL');
      return;
    }
    setDeviceLoading(true);
    axios.get(DEVICE_API_URL, { params: { factory: selectedFactory, custCd: selectedCust } })
    .then(res => { 
      const fetchedDevices = res.data.data || [];
      // 'ALL' 옵션 객체를 생성합니다.
      const allOption = { CODE_CD: 'ALL' };
      // 받아온 목록 맨 앞에 'ALL' 옵션을 추가하여 상태를 업데이트합니다.
      setDeviceList([allOption, ...fetchedDevices]);
      setSelectedDevice('ALL'); })
      .catch(e => { console.error("DEVICE 목록 로드 오류:", e); setDeviceList([]); })
      .finally(() => setDeviceLoading(false));
  }, [selectedFactory, selectedCust]);

  useEffect(() => {
    if (user?.role === "CUST" && custList.length > 0) {
      const found = custList.find(c => c.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
      if (found) setSelectedCust(found.CODE_CD);
      else {
        setSelectedCust(null);
        notify(`사용자(${user.username})에 해당하는 고객사 코드가 현재 Factory에 없습니다.`, 'warning', 3000);
      }
    }
  }, [user, custList]);

  const fetchData = useCallback(() => {
    if (!selectedFactory || !selectedCust) {
      notify("Factory와 CUST는 필수 선택 항목입니다.", "warning", 2000);
      return;
    }
    setLoading(true);
    setError("");
    setData([]);

    const params = {
      factory: selectedFactory,
      custCd: selectedCust,
      device: selectedDevice,
      dateFrom: formatDate(dateFrom),
      dateTo: formatDate(dateTo),
    };
    
    axios.get(HARD_BIN_API_URL_NEW, { params })
      .then(res => {
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        
        const mappedData = (res.data.data || []).map((row, idx) => ({
          ...row,
          id: idx,
        }));
        setData(mappedData);
      })
      .catch(e => {
        const errorMessage = e.response?.data?.retMsg || e.message || "알 수 없는 오류";
        setError(`조회 오류: ${errorMessage}`);
        setData([]);
      })
      .finally(() => setLoading(false));
  }, [selectedFactory, selectedCust, selectedDevice, dateFrom, dateTo]);

  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Hard_Bin_Summary");
    exportDataGrid({
        component: e.component,
        worksheet: worksheet,
        autoFilterEnabled: true
    }).then(() => {
        workbook.xlsx.writeBuffer().then(buffer => {
        saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Hard_Bin_Summary.xlsx");
        });
    });
    e.cancel = true;
  };

  const handleFactoryChange = (e) => {
    setSelectedFactory(e.value);
    setSelectedCust(null);
  };

  const handleCustChange = (e) => {
    setSelectedCust(e.value);
  };

  const onRowPrepared = (e) => {
    if (e.rowType === 'data') {
      if (e.data.GRPID > 0) {
        e.rowElement.style.fontWeight = 'bold';
        e.rowElement.style.backgroundColor = '#f5f5f5';
      }
    }
  };

  return (
    <div style={{ padding: 20, fontFamily: 'Arial, sans-serif', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>Hard Bin Summary</h3>
        <div style={{ display: "flex", gap: 15, flexWrap: "wrap", alignItems: 'center' }}>
          <DateBox value={dateFrom} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateFrom(e.value)} label="FROM" labelMode="floating" width={180} />
          <DateBox value={dateTo} displayFormat="yyyy-MM-dd"  type="date" onValueChanged={e => setDateTo(e.value)} label="TO" labelMode="floating" width={180} />
          <SelectBox items={['FT01']} value={selectedFactory}   onValueChanged={handleFactoryChange}  label="FACTORY *" labelMode="floating" width={150} />
          <SelectBox dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_NM" onValueChanged={handleCustChange} disabled={!selectedFactory || custLoading || user?.role === "CUST"} searchEnabled showClearButton={user?.role !== "CUST"} placeholder="Select CUST" label="CUST *" labelMode="floating" width={220} noDataText={custLoading ? '불러오는 중...' : '해당 Factory의 고객사가 없습니다.'} />
          <SelectBox dataSource={deviceList} value={selectedDevice} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedDevice(e.value)} disabled={!selectedCust || deviceLoading} searchEnabled label="DEVICE" labelMode="floating" width={220} noDataText={deviceLoading ? '불러오는 중...' : '해당 고객사의 Device가 없습니다.'} />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "#d9534f", fontWeight: 'bold' }}>{error}</p>}
      </div>
      
      {/* 2. 그리드 영역 (남은 공간을 모두 차지) */}
      <div style={{ flex: 1, position: 'relative' }}> {/* [수정] 중복 div 제거 및 position: 'relative' 추가 */}
        {/* 로딩 인디케이터를 그리드 영역 안으로 이동 */}
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading...</div>
          </div>
        )}
        <DataGrid
          dataSource={data}
          keyExpr="id"
          onExporting={onExporting}
          onRowPrepared={onRowPrepared}
          height="100%"
          showBorders={true}
          columnAutoWidth={true}
          allowColumnResizing={true}
          hoverStateEnabled={true}
        >
          <Paging defaultPageSize={10} />
          <Export enabled={true} allowExportSelectedData={false} />          
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />   
          <SearchPanel visible={true} width={240} highlightCaseSensitive={false} />
          <Sorting mode="multiple" />
    
          <Column dataField="CUSTOMER" caption="Customer" fixed={true} allowSorting={false} />
          <Column dataField="MAT_ID" caption="Mat Id" fixed={true} />
          <Column dataField="LOT_ID" caption="Lot Id"  fixed={true}/>
          <Column dataField="TRAN_TIME" caption="Tran Time"  fixed={true}/>
          <Column dataField="OPER" caption="Oper" fixed={true} />
          <Column dataField="TEST_TIME" caption="Test Time" dataType="number" alignment="right" format="#,##0.00" />
          <Column dataField="PARA" caption="Para" />
          <Column dataField="INDEX_TIME" caption="Index Time" dataType="number" alignment="right" format="#,##0.0" />
          <Column dataField="START_RES_ID" caption="Start Res Id" />
          <Column dataField="IN_QTY" caption="In Qty" dataType="number" alignment="right" />
          <Column dataField="OUT_QTY" caption="Out Qty" dataType="number" alignment="right" />
          <Column dataField="R_QTY" caption="R Qty" dataType="number" alignment="right" />
          <Column dataField="BIN1" caption="Bin1" dataType="number" alignment="right" width={80} />
          <Column dataField="BIN2" caption="Bin2" dataType="number" alignment="right" width={80} />
          <Column dataField="BIN3" caption="Bin3" dataType="number" alignment="right" width={80} />
          <Column dataField="BIN4" caption="Bin4" dataType="number" alignment="right" width={80} />
          <Column dataField="BIN5" caption="Bin5" dataType="number" alignment="right" width={80} />
          <Column dataField="BIN6" caption="Bin6" dataType="number" alignment="right" width={80} />
          <Column dataField="BIN7" caption="Bin7" dataType="number" alignment="right" width={80} />
          <Column dataField="BIN8" caption="Bin8" dataType="number" alignment="right" width={80} />
          <Column dataField="BIN9" caption="Bin9" dataType="number" alignment="right" width={80} />
          <Column dataField="PGM" caption="Pgm" />
        </DataGrid>
      </div>
    </div>
  );
}